import {
  ApolloClient,
  split,
  HttpLink,
  ApolloLink,
  InMemoryCache,
  fromPromise
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';

import AppConfig from '@config';

import { getStorageItem, STORAGE_ACTION } from '@utils/storage';
import {
  GRAPHQL_URL,
  subscriptionClient,
  setSentryUserInfo,
  clearAuthInfo,
  tokenRefresh,
  getOperationName
} from './utils';
import jwtDecode from '@utils/jwtDecode';

import * as Sentry from '@sentry/react';

import introspectionQueryResultData from '@generated/fragmentTypes.json';
import { CapaErrorCode } from '@generated/graphql';
import cache from './cache';

const ignoredErrorCodes: CapaErrorCode[] = [
  CapaErrorCode.NotAllowedUser,
  CapaErrorCode.PasswordIncorrect,
  CapaErrorCode.DuplicateCompanyRegistrationNo
];

// suppport http protocol for local servers
const protocol = AppConfig.mode === 'local' ? 'http' : 'https';
const httpLink = new HttpLink({
  uri: window.location.host.includes('bluegreen')
    ? `${protocol}://capa-api-bluegreen.capa.ai/v1/graphql`
    : `${protocol}://${GRAPHQL_URL}`
});

const tokenMisMatchLink = new ApolloLink((operations, forward) => {
  return forward(operations).map((operation) => {
    const checkMismatchQuries = [
      'fetchQuotationChatRoom',
      'fetchQuotationUserRfqDetail',
      'fetchMyRfqQuotations',
      'fetchConfirmDetailedQuotation',
      'fetchPurchaseOrders',
      'fetchPurchaseOrdersWithItem'
    ];

    const operationName = getOperationName(operation);

    if (checkMismatchQuries.includes(operationName)) {
      if (
        operation.data?.[operationName]?.result === CapaErrorCode.TokenMismatch
      ) {
        window.location.href = `${window.location.origin}/notfound`;
      }
    }

    return operation;
  });
});

const authLink = new ApolloLink((operations, forward) => {
  const accessToken = getStorageItem(STORAGE_ACTION.CLIENT_ACCESS_TOKEN);

  const decoded = jwtDecode();

  const context = operations.getContext() ?? {};
  // Inject Header Authorization Token

  if (context.headers && 'ip' in context.headers && context.headers.ip) {
    operations.setContext({
      headers: {
        authorization: null,
        ip: context.headers.ip
      }
    });
  } else {
    operations.setContext({
      headers: {
        authorization: `Bearer ${accessToken}`,
        ip: null
      }
    });
  }

  return forward(operations).map((operation) => {
    const queryName = operation.data
      ? Object.keys(operation.data)[0]
      : undefined;

    const capaErrorCode = queryName
      ? operation.data && (operation.data[queryName]?.result as CapaErrorCode)
      : undefined;

    const isError = capaErrorCode !== CapaErrorCode.Success;

    if (capaErrorCode && ignoredErrorCodes.includes(capaErrorCode)) {
      return operation;
    }

    // 요청은 성공했지만(2xx) 클라이언트의 잘못된 요청(invalid input value, ...)으로 서버에서 에러를 줄 경우
    if (isError && queryName) {
      setSentryUserInfo(decoded, queryName);

      Sentry.withScope((scope) => {
        scope.setLevel(Sentry.Severity.Log);
        scope.setContext('response', { ...operation });
        scope.setContext('variables', operations.variables);
        scope.setContext('error message', {
          message: operation.data?.[queryName]?.errorMessage ?? ''
        });
        Sentry.captureMessage(
          `${queryName}/${operation.data?.[queryName]?.result}`
        );
        scope.clear();
      });
    }

    return operation;
  });
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, response, forward }) => {
    const decoded = jwtDecode();

    setSentryUserInfo(decoded, operation.operationName);

    if (graphQLErrors && graphQLErrors.length > 0) {
      for (const error of graphQLErrors) {
        const { message } = error;

        /* 토큰이 만료된 경우 refresh 실행 */
        if (message === 'TOKEN_EXPIRED' || message.includes('JWTExpired')) {
          // 웹 소켓 종료, 새로운 헤더로 재시작
          subscriptionClient.close(false, true);

          const prevHeaders = operation.getContext()?.headers;

          // onError 함수내에서 비동기 함수 처리
          return fromPromise(tokenRefresh().then((token) => token))
            .filter((token) => Boolean(token))
            .flatMap((token) => {
              // operation의 header를 재설정
              operation.setContext(() => ({
                headers: {
                  ...prevHeaders,
                  authorization: `Bearer ${token}`
                }
              }));

              // 실패한 요청 재실행
              return forward(operation);
            });
        }

        /* 토큰이 없거나, 잘못된 토큰인 경우 인증관련 내용 삭제 및 login 페이지로 이동 */
        if (message === 'INVALID_TOKEN') {
          return clearAuthInfo();
        }
      }

      // console.log(response);
      // console.log(graphQLErrors);
      // console.log(networkError);
      // console.log(operation);

      if (
        operation?.query.definitions[0].kind === 'OperationDefinition' &&
        operation?.query.definitions[0].operation === 'mutation'
      ) {
        Sentry.withScope((scope) => {
          scope.setLevel(Sentry.Severity.Error);
          scope.setContext('error data', { ...graphQLErrors[0] });
          scope.setContext('response', {
            data: response?.data,
            errors: response?.errors,
            extensions: response?.extensions
          });
          scope.setContext('operation', { operation });
          Sentry.captureMessage(
            `${operation.operationName}/Error : ${graphQLErrors[0].message}`
          );
          scope.clear();
        });
      }

      // eslint-disable-next-line no-console
      if (
        networkError &&
        operation?.query.definitions[0].kind === 'OperationDefinition' &&
        operation?.query.definitions[0].operation === 'mutation'
      ) {
        console.error(
          '[Network error]:',
          networkError,
          '[operation]:',
          operation
        );

        Sentry.withScope((scope) => {
          scope.setLevel(Sentry.Severity.Error);
          scope.setContext('network error', {
            message: networkError.message,
            stack: networkError.stack,
            name: networkError.name
          });
          scope.setContext('response', {
            data: response?.data,
            errors: response?.errors,
            extensions: response?.extensions
          });
          scope.setContext('operation', { ...operation });
          Sentry.captureMessage(
            `${operation.operationName}/Network error : ${networkError.message}`
          );
          scope.clear();
        });
      }
    }
  }
);

const httpAuthLink = authLink.concat(httpLink);

const wsLink = new WebSocketLink(subscriptionClient);

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  tokenMisMatchLink.concat(httpAuthLink)
);

const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, link]),
  cache: new InMemoryCache(
    Object.assign(
      {},
      {
        possibleTypes: introspectionQueryResultData.possibleTypes
      },
      cache
    )
  ),
  connectToDevTools: AppConfig.env !== 'production' ? true : false
});

export default apolloClient;
